import type { ReactNode } from 'react';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { SignLimitOrdersErrorCodes } from '@sorare/wallet-shared';
import type {
  ApproveAuthorizationRequests,
  ApproveMigrator,
  CreateWalletRecovery,
  GetEscapeHatchSignatures,
  LoadWallet,
  LogOut,
  Password,
  PasswordForgotten,
  PrivateKeyRecoveryPayload,
  Prompt,
  PromptDeposit,
  PromptRestoreWallet,
  PromptSignupWithUsername,
  RequestOAuth2,
  RequestResize,
  SignMigration,
  SignTransfer,
  SignUpMobileView,
  SignWalletChallenge,
  Sport as WalletSport,
} from '@sorare/wallet-shared';
import type { AuthorizationRequest } from '@sorare/wallet-shared/src/contexts/messaging/authorizations';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { useCheckPhoneNumberVerificationCode } from '#src/components/user/VerifyPhoneNumber/useCheckPhoneNumberVerificationCode.ts';
import type {
  UpdateUserAttributes,
  UpdateUserEmailAttributes,
} from '#src/contexts/auth/index.ts';
import { useAuthContext } from '#src/contexts/auth/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import type {
  OAuth2RequestHandler,
  Transfer,
  WalletPlaceHolderRequestedSize,
  WalletPlaceHolderResizeHandler,
} from '#src/contexts/wallet/index.ts';
import {
  WalletContext,
  useMessagingContext,
} from '#src/contexts/wallet/index.ts';
import {
  WalletTab,
  useWalletDrawerContext,
} from '#src/contexts/walletDrawer/index.tsx';
import { useLogout as useLogOut } from '#src/hooks/auth/useLogOut.ts';
import type { RecoveryOption } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { useWalletNeedsRecover } from '#src/hooks/recovery/useWalletNeedsRecover.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useQueryString } from '#src/hooks/useQueryString.ts';
import { useNeedsCreateEthWallet } from '#src/hooks/wallets/useNeedsCreateEthWallet.ts';
import { sendSafeError } from '#src/lib/error.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';

import { usePromptResetPassword } from './usePromptResetPassword.ts';
import { useSetSolanaAddress } from './useSetSolanaAddress.tsx';
import { useUpdateUserEmail } from './useUpdateUserEmail.ts';

interface Props {
  children: ReactNode;
  setWindow: (window: Window | undefined) => void;
}

const SPORTS: Record<WalletSport, Sport> = {
  FOOTBALL: Sport.FOOTBALL,
  BASEBALL: Sport.BASEBALL,
  NBA: Sport.NBA,
};

const messages = defineMessages({
  confirmDevice: {
    defaultMessage: 'Please verify your device first.',
  },
  recoverWallet: {
    defaultMessage: 'Please recover your wallet to continue.',
  },
});

const LoginCallbacks = () => {
  const { currentUser } = useCurrentUserContext();

  const action = useQueryString('action');
  const walletNeedsRecover = useWalletNeedsRecover();
  const needsCreateEthWallet = useNeedsCreateEthWallet();
  const setSolanaAddress = useSetSolanaAddress();

  const allow =
    currentUser?.confirmedDevice &&
    !walletNeedsRecover &&
    !needsCreateEthWallet;

  const needsCreateSolanaAddress =
    currentUser && !currentUser.wallet?.solanaAddress;

  const promptSetSolanaAddress = useCallback(async () => {
    await setSolanaAddress();
  }, [setSolanaAddress]);

  useEffect(() => {
    if (!allow) return;
    if (action !== 'resetPassword') {
      (async () => {
        if (needsCreateSolanaAddress) {
          await promptSetSolanaAddress();
        }
      })();
    }
  }, [allow, action, needsCreateSolanaAddress, promptSetSolanaAddress]);

  return null;
};

export const Wallet = ({ children, setWindow }: Props) => {
  const { sendRequest, registerHandler } = useMessagingContext();
  const track = useEvents();
  const { formatMessage } = useIntl();
  const walletNeedsRecover = useWalletNeedsRecover();
  const needsCreateEthWallet = useNeedsCreateEthWallet();
  const [promptDimensions, setPromptDimensions] = useState<
    { promptType: string; size?: WalletPlaceHolderRequestedSize } | undefined
  >();
  const [dimensionsHandlers, updateDimensionsListener] = useState<
    WalletPlaceHolderResizeHandler[]
  >(() => []);
  const oauthHandlers = useRef<OAuth2RequestHandler[]>([]);
  const { updateUser } = useAuthContext();
  const logOutApp = useLogOut();
  const action = useQueryString('action');
  const [walletNode, setWalletNode] = useState<Element | null>(null);
  const [selectedRecoveryOption, setSelectedRecoveryOption] =
    useState<RecoveryOption | null>(null);
  const { showWallet, setCurrentTab, closeWalletAndDrawer, hideWallet } =
    useWalletDrawerContext();
  const { currentUser, refetch } = useCurrentUserContext();
  const { showNotification } = useSnackNotificationContext();
  const updateUserEmail = useUpdateUserEmail();
  const checkPhoneNumberVerificationCode =
    useCheckPhoneNumberVerificationCode();
  const { isMobileApp, isAndroidApp, postMessage } = useIsMobileApp();
  const setSolanaAddress = useSetSolanaAddress();

  const allow =
    currentUser?.confirmedDevice &&
    !walletNeedsRecover &&
    !needsCreateEthWallet;

  const prompt = useCallback(
    async (type: Prompt['request']['args']['type']) => {
      setPromptDimensions(prevValue => {
        // clear previously stored value if changing the prompt type
        if (prevValue?.promptType !== type) {
          return {
            promptType: type,
          };
        }
        return prevValue;
      });
      await sendRequest<Prompt>('prompt', { type });
    },
    [sendRequest]
  );

  const promptDeposit = useCallback(
    async (id: any) => {
      if (allow) {
        await sendRequest<PromptDeposit>('promptDeposit', { id });
        showWallet();
      }
    },
    [allow, sendRequest, showWallet]
  );

  const signIn = useCallback(async () => {
    if (isAndroidApp) {
      // Guard to prevent displaying login form from mobileApp
      postMessage('logout', { isExplicit: false });
    } else {
      prompt('signIn');
    }
  }, [isAndroidApp, postMessage, prompt]);

  const signUp = useCallback(
    async (username: string, sport: WalletSport) => {
      await sendRequest<PromptSignupWithUsername>('promptSignupWithUsername', {
        username,
        isAndroidApp,
        sport,
      });
    },
    [isAndroidApp, sendRequest]
  );

  const signUpMobileView = useCallback(async () => {
    await sendRequest<SignUpMobileView>('signUpMobileView', {
      isAndroidApp,
    });
  }, [isAndroidApp, sendRequest]);

  const passwordForgotten = useCallback(async () => {
    track('Click Password Forgotten');
    await sendRequest<PasswordForgotten>('passwordForgotten', {
      isMobileApp,
    });
  }, [isMobileApp, sendRequest, track]);

  const promptResetPassword = usePromptResetPassword();

  const logOut = useCallback(async () => {
    await sendRequest<LogOut>('logOut', {
      flushMessagingQueue: true,
    });
    logOutApp();
  }, [logOutApp, sendRequest]);

  const getPassword = useCallback<
    (requestArgs?: Password['request']['args']) => Promise<string | undefined>
  >(
    async (requestArgs = {}) => {
      showWallet();
      setCurrentTab(WalletTab.GET_PASSWORD);

      const {
        result: { passwordHash },
      } = await sendRequest<Password>('password', requestArgs);

      closeWalletAndDrawer();
      return passwordHash;
    },
    [closeWalletAndDrawer, sendRequest, showWallet, setCurrentTab]
  );

  const promptRestoreWallet = useCallback(
    async (
      privateKeyRecoveryPayloads: PrivateKeyRecoveryPayload[],
      recoveryOption?: RecoveryOption
    ) => {
      setCurrentTab(WalletTab.RESTORE_WALLET);
      setSelectedRecoveryOption(recoveryOption || null);
      showWallet();

      await sendRequest<PromptRestoreWallet>('promptRestoreWallet', {
        privateKeyRecoveryPayloads,
        privateKeyRecoveryPayload: privateKeyRecoveryPayloads[0],
      });
    },
    [sendRequest, showWallet, setCurrentTab]
  );

  const handleWalletSuccessullyRecovered = useCallback(async () => {
    await refetch();
    setCurrentTab(WalletTab.HOME);
    setSelectedRecoveryOption(null);
    showNotification('walletSuccessfullyRecovered');
    hideWallet();
  }, [refetch, setCurrentTab, showNotification, hideWallet]);

  const checkUserPhoneNumberVerificationCodeWithRecovery = useCallback(
    async (code: string): Promise<{ message: string }[] | null> => {
      if (!currentUser) throw new Error('Missing current user');
      if (!currentUser.unverifiedPhoneNumber)
        throw new Error('No pending phone verification');

      const { result, error } = await sendRequest<CreateWalletRecovery>(
        'createWalletRecovery',
        {
          recoveryMethod: 'phone',
          recoveryDestination: currentUser.unverifiedPhoneNumber,
        }
      );

      if (error) {
        return [
          {
            message: error,
          },
        ];
      }

      if (!result) {
        return [
          {
            message: formatMessage({
              defaultMessage: 'Unable to generate recovery key.',
            }),
          },
        ];
      }

      closeWalletAndDrawer();

      return checkPhoneNumberVerificationCode(code, result.privateKeyRecovery);
    },
    [
      formatMessage,
      sendRequest,
      checkPhoneNumberVerificationCode,
      currentUser,
      closeWalletAndDrawer,
    ]
  );

  const loadWallet = useCallback(async () => {
    if (!currentUser?.confirmedDevice) {
      showWallet();
      return {
        errors: [
          {
            message: formatMessage(messages.confirmDevice),
          },
        ],
      };
    }
    if (walletNeedsRecover) {
      setCurrentTab(WalletTab.HOME);
      showWallet();
      return {
        errors: [{ message: formatMessage(messages.recoverWallet) }],
      };
    }
    const { result, error } = await sendRequest<LoadWallet>('loadWallet', {});
    if (error) {
      return {
        errors: [{ message: error }],
      };
    }

    if (!result) {
      return {
        errors: [
          {
            message: formatMessage({
              defaultMessage: 'You must first unlock your wallet.',
            }),
          },
        ],
      };
    }
    closeWalletAndDrawer();
    return {};
  }, [
    formatMessage,
    sendRequest,
    closeWalletAndDrawer,
    currentUser?.confirmedDevice,
    showWallet,
    walletNeedsRecover,
    setCurrentTab,
  ]);

  const updateUserEmailWithPassword = useCallback(
    async (attributes: UpdateUserEmailAttributes) => {
      if (!currentUser) throw new Error('Missing current user');

      if (!attributes.email || attributes.email === currentUser.email) {
        return { errors: [] };
      }

      const { result, error } = await sendRequest<CreateWalletRecovery>(
        'createWalletRecovery',
        { recoveryMethod: 'email', recoveryDestination: attributes.email }
      );

      if (error) {
        return {
          errors: [
            {
              message: error,
            },
          ],
        };
      }

      if (!result) {
        return {
          errors: [
            {
              message: formatMessage({
                defaultMessage: 'Unable to generate recovery key.',
              }),
            },
          ],
        };
      }

      closeWalletAndDrawer();

      attributes.privateKeyRecovery = result.privateKeyRecovery;
      return updateUserEmail({ ...attributes });
    },
    [
      formatMessage,
      updateUserEmail,
      sendRequest,
      currentUser,
      closeWalletAndDrawer,
    ]
  );

  const updateUserWithPassword = useCallback(
    async (attributes: UpdateUserAttributes) => {
      if (!currentUser) throw new Error('Missing current user');
      const currentPasswordHash = await getPassword();
      if (currentPasswordHash) {
        return updateUser({ ...attributes, currentPasswordHash });
      }

      return {
        errors: [
          formatMessage({
            defaultMessage: 'You must first unlock your wallet.',
          }),
        ],
      };
    },
    [currentUser, getPassword, formatMessage, updateUser]
  );

  const approveMigrator = useCallback(
    async (nonce: string | number) => {
      if (!allow) {
        showWallet();
        return undefined;
      }
      const { result } = await sendRequest<ApproveMigrator>('approveMigrator', {
        nonce,
      });

      closeWalletAndDrawer();
      return result;
    },
    [sendRequest, closeWalletAndDrawer, showWallet, allow]
  );

  const signMigration = useCallback(
    async (cardIds: string[], expirationBlock: number | string) => {
      if (!allow) {
        showWallet();
        return undefined;
      }
      const { result } = await sendRequest<SignMigration>('signMigration', {
        cardIds,
        expirationBlock,
      });

      closeWalletAndDrawer();
      return result?.signature;
    },
    [sendRequest, closeWalletAndDrawer, showWallet, allow]
  );

  const signTransfer = useCallback(
    async (transfer: Transfer) => {
      if (!allow) {
        showWallet();
        return null;
      }
      const { result } = await sendRequest<SignTransfer>('signTransfer', {
        transfer,
      });

      closeWalletAndDrawer();
      return result?.signature ? JSON.stringify(result.signature) : null;
    },
    [sendRequest, closeWalletAndDrawer, showWallet, allow]
  );

  const getEscapeHatchSignatures = useCallback(
    async ({ fee, feeRecipient }: { fee: bigint; feeRecipient: string }) => {
      if (!allow) {
        showWallet();
        return undefined;
      }
      const { result } = await sendRequest<GetEscapeHatchSignatures>(
        'getEscapeHatchSignatures',
        { fee: fee.toString(), feeRecipient }
      );
      closeWalletAndDrawer();
      return result;
    },
    [sendRequest, closeWalletAndDrawer, showWallet, allow]
  );

  const approveAuthorizationRequests = useCallback(
    async (authorizationRequests: AuthorizationRequest[]) => {
      if (!allow) {
        showWallet();
        return {
          approvals: undefined,
          starkKey: undefined,
        };
      }
      const { result, error } = await sendRequest<ApproveAuthorizationRequests>(
        'approveAuthorizationRequests',
        {
          authorizationRequests,
        }
      );
      if (currentUser.wallet && !currentUser.wallet?.solanaAddress) {
        setSolanaAddress();
      }
      closeWalletAndDrawer();
      if (error) {
        if (error.code !== SignLimitOrdersErrorCodes.PROMPT_CANCELED) {
          sendSafeError(error.message);
        }
        return {
          approvals: undefined,
          starkKey: undefined,
        };
      }
      return {
        authorizationApprovals: result?.authorizationApprovals,
        starkKey: result?.starkKey,
      };
    },
    [
      currentUser,
      setSolanaAddress,
      sendRequest,
      showWallet,
      allow,
      closeWalletAndDrawer,
    ]
  );

  const signWalletChallenge = useCallback(
    async (challenge: string) => {
      if (!allow) {
        showWallet();
        return null;
      }
      const { result } = await sendRequest<SignWalletChallenge>(
        'signWalletChallenge',
        { challenge }
      );

      closeWalletAndDrawer();
      return result?.signature || null;
    },
    [sendRequest, closeWalletAndDrawer, showWallet, allow]
  );

  const onActionChange = useEffectEvent((actionArg: Nullable<string>) => {
    if (actionArg === 'signup') {
      if (isAndroidApp) {
        // Guard to prevent displaying login form from mobileApp
        postMessage('logout', { isExplicit: false });
      } else {
        prompt('signup');
      }
    } else if (actionArg === 'signin') {
      signIn();
    }
  });

  useEffect(() => {
    onActionChange(action);
  }, [action]);

  useEffect(
    () =>
      registerHandler<RequestResize>(
        'requestPlaceholderResize',
        async dimensions => {
          setPromptDimensions(prevValue => {
            if (prevValue) {
              return {
                ...prevValue,
                size: dimensions,
              };
            }
            return prevValue;
          });
          return {};
        }
      ),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<RequestOAuth2>(
        'requestOAuth',
        async ({
          platform,
          sport,
          signup,
          nickname,
          acceptTerms,
          acceptAgeLimit,
          acceptToShareAll,
          acceptToShareSpecific,
        }) => {
          if (signup) {
            track('Click Signup By O Auth', { provider: platform });
          }
          await Promise.all(
            oauthHandlers.current.map(async handler =>
              handler(platform, {
                nickname,
                sport: sport && SPORTS[sport],
                acceptTerms,
                acceptAgeLimit,
                acceptToShareAll,
                acceptToShareSpecific,
              })
            )
          );
          return {};
        }
      ),
    [registerHandler, track]
  );

  const registerOnWalletResizeRequestHandler = useCallback((callback: any) => {
    updateDimensionsListener(listeners => {
      return [...listeners, callback];
    });
    return () => {
      updateDimensionsListener(listeners => {
        const index = listeners.indexOf(callback);
        if (index >= 0) {
          return [...listeners].splice(index, 1);
        }
        return listeners;
      });
    };
  }, []);

  const registerOAuthHandler = useCallback((callback: OAuth2RequestHandler) => {
    oauthHandlers.current.push(callback);
    return () => {
      const index = oauthHandlers.current.indexOf(callback);
      if (index >= 0) {
        oauthHandlers.current.splice(index, 1);
      }
    };
  }, []);

  useEffect(() => {
    const size = promptDimensions?.size;
    if (size) {
      dimensionsHandlers.forEach(handler => handler(size));
    }
  }, [promptDimensions, dimensionsHandlers]);

  useEffect(
    () =>
      registerHandler<PasswordForgotten>('passwordForgotten', async () => {
        return passwordForgotten().then(() => ({}));
      }),
    [registerHandler, passwordForgotten]
  );

  const value = useMemo(
    () => ({
      approveAuthorizationRequests,
      setWindow,
      signIn,
      signUp,
      signUpMobileView,
      passwordForgotten,
      promptResetPassword,
      logOut,
      loadWallet,
      prompt,
      promptDeposit,
      promptRestoreWallet,
      selectedRecoveryOption,
      handleWalletSuccessullyRecovered,
      getPassword,
      checkUserPhoneNumberVerificationCodeWithRecovery,
      updateUserEmailWithPassword,
      updateUserWithPassword,
      approveMigrator,
      signMigration,
      signTransfer,
      getEscapeHatchSignatures,
      signWalletChallenge,
      walletNode,
      setWalletNode,
      setOnWalletResizeRequest: registerOnWalletResizeRequestHandler,
      registerOAuthHandler,
    }),
    [
      approveAuthorizationRequests,
      approveMigrator,
      checkUserPhoneNumberVerificationCodeWithRecovery,
      getPassword,
      handleWalletSuccessullyRecovered,
      logOut,
      loadWallet,
      passwordForgotten,
      prompt,
      promptDeposit,
      promptResetPassword,
      promptRestoreWallet,
      registerOAuthHandler,
      registerOnWalletResizeRequestHandler,
      selectedRecoveryOption,
      setWindow,
      signIn,
      signMigration,
      signTransfer,
      getEscapeHatchSignatures,
      signUp,
      signUpMobileView,
      signWalletChallenge,
      updateUserEmailWithPassword,
      updateUserWithPassword,
      walletNode,
    ]
  );

  return (
    <WalletContext value={value}>
      <LoginCallbacks />
      {children}
    </WalletContext>
  );
};
